import React, { useState, useEffect } from "react";
import { Modal, FormInput, FormSelect, FormTextarea, Button, FileUpload, ConfirmationModal, SearchableSelect } from "../../../components/ui";
import { InspectionAddForm } from "./InspectionAddForm";
import { SiteVisitWizard } from "./SiteVisitWizard";
import toast from "react-hot-toast";
import { showApiError } from "@/lib/toast";
import { Calendar, DollarSign, CheckCircle, Info, ShieldCheck, User, AlertTriangle } from "lucide-react";
import {
  appointmentSchema,
  quotationSchema,
  subsidySchema,
  assignmentSchema
} from "../validations/accountStage.schema";

export const ChangeStatusForm = ({ pendingStatus, onClose, onConfirm, stages, teamMembers = [], user }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [showEmailConfirm, setShowEmailConfirm] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [targetStatus, setTargetStatus] = useState("");

  const stageNoteFields = {
    'contacted': 'contactedNote',
    'appointment_scheduled': 'appointmentNote',
    'site_visit': 'inspectionNote',
    'inspection_done': 'inspectionNote',
    'quotation_sent': 'quotationNote',
    'approved': 'approvedNote',
    'drop': 'droppedNote',
    'new': 'notes'
  };

  const getTargetField = (status) => stageNoteFields[status] || 'notes';

  const roleStr = (user?.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
  const isSalesExec = roleStr === "sales-exec" || roleStr === "sale-exec";

  // Filter only sales executives who match the account's location
  const salesExecs = (teamMembers || []).filter(u => {
    const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
    const isExec = uRole === 'sales-exec' || uRole === 'sale-exec' || uRole === 'sales-executive' || uRole === 'sales';

    const accountLoc = (pendingStatus?.account?.city || pendingStatus?.account?.location || "").toLowerCase().trim();
    const accountOffice = (pendingStatus?.account?.officeLocation || "").toLowerCase().trim();
    const userLoc = (u.workLocation || u.officeLocation || "").toLowerCase().trim();

    // Lenient match: true if either location is empty, OR if they match, OR if one contains the other
    const matchesLocation = !accountLoc || !userLoc ||
      userLoc.includes(accountLoc) ||
      accountLoc.includes(userLoc) ||
      (accountOffice && userLoc.includes(accountOffice));

    return isExec && matchesLocation;
  });

  const emailVal = (pendingStatus?.account?.email || "").trim().toLowerCase();
  const isMissingEmail = !emailVal || ["n/a", "undefined", "null"].includes(emailVal);
  const isInvalidFormat = !isMissingEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailVal);
  const isSuspicious = !isMissingEmail && !isInvalidFormat && (emailVal.split('@')[0].length < 3);
  const hasEmailIssue = isMissingEmail || isInvalidFormat || isSuspicious;

  useEffect(() => {
    if (pendingStatus) {
      const { account, targetStatus } = pendingStatus;
      setTargetStatus(targetStatus);
      setShowEmailConfirm(false);

      // Initialize form based on target status
      if (targetStatus === "appointment_scheduled") {
        setFormData({
          appointmentDate: account.appointmentDate ? new Date(account.appointmentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          notes: ""
        });
      } else if (targetStatus === "quotation_sent") {
        const capacity = account.SDCapacityRequested || account.capacity || 1;
        setFormData({
          quotationAmount: account.quotationAmount || (capacity * 70000),
          SDCapacity: capacity,
          notes: "",
          quotationFile: []
        });
      } else if (targetStatus === "site_visit" || targetStatus === "inspection_done") {
        setFormData({
          propertyAddress: account.propertyAddress || account.address || account.addressLine1 || '',
          propertySize: account.propertySize || account.propertyType || '',
          projectType: account.projectType || '',
          propertyType: account.propertyType || '',
          floorDetails: account.floorDetails || '',
          existingPowerConsumption: account.existingPowerConsumption || '',
          SDCapacityRequested: account.SDCapacityRequested || account.capacity || '',
          existingPowerPaid: account.existingPowerPaid || '',
          notes: ""
        });
      } else if (targetStatus === "project_started") {
        setFormData({
          hasSubsidy: account.hasSubsidy || "",
          subsidyId: account.subsidyId || "",
          notes: ""
        });
      } else if (targetStatus === "assigned") {
        setFormData({
          assignedTo: isSalesExec ? user?._id : (account.assignedTo?._id || account.assignedTo || ""),
          notes: ""
        });
      } else {
        setFormData({ notes: "" });
      }
    }
  }, [pendingStatus]);

  if (!pendingStatus) return null;

  const { account, targetStatus: stageKey } = pendingStatus;
  const stage = stages.find(s => s.key === stageKey) || { label: stageKey };
  const fullName = account?.customerName || `${account?.firstName || ""} ${account?.lastName || ""}`.trim() || "No Name";
  const currentStatus = account?.accountStatus || account?.status || "new";

  const processSubmission = async (data) => {
    // Schema-based validations FIRST
    const targetField = getTargetField(targetStatus);
    const submissionData = { ...data };
    let validationResult = { success: true, data: submissionData };

    if (targetStatus === "appointment_scheduled") {
      validationResult = appointmentSchema.safeParse(submissionData);
    } else if (targetStatus === "quotation_sent") {
      validationResult = quotationSchema.safeParse(submissionData);
    } else if (targetStatus === "project_started") {
      validationResult = subsidySchema.safeParse(submissionData);
    } else if (targetStatus === "assigned") {
      validationResult = assignmentSchema.safeParse(submissionData);
    }

    if (!validationResult.success) {
      const errMsg = validationResult.error?.errors?.[0]?.message || validationResult.error?.issues?.[0]?.message || "Validation failed";
      toast.error(errMsg);
      return;
    }

    const finalData = validationResult.data;

    // Now check for email issue
    if ((targetStatus === 'quotation' || targetStatus === 'quotation_sent') && hasEmailIssue && !showEmailConfirm) {
      setConfirmData(finalData);
      setShowEmailConfirm(true);
      return;
    }

    setLoading(true);
    if (showEmailConfirm) setShowEmailConfirm(false);

    try {

      // Map notes to specific fields
      if (finalData.notes) {
        finalData[targetField] = finalData.notes;
        finalData.activityNote = finalData.notes;
      }

      await onConfirm(finalData);
      onClose();
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    await processSubmission(formData);
  };

  const commonNotes = (
    <FormTextarea
      label="Transition Notes / Activity Comment"
      placeholder="What happened? Why the change?"
      value={formData.notes || ""}
      onChange={e => setFormData({ ...formData, notes: e.target.value })}
      rows={3}
    />
  );

  const renderFields = () => {
    switch (targetStatus) {
      case "appointment_scheduled":
        return (
          <div className="space-y-4">
            <div className="p-4 bg-orange-50 rounded-xl border border-orange-100 flex items-start gap-3">
              <div className="p-2 bg-orange-100 text-orange-600 rounded-lg">
                <Calendar size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-orange-800">Schedule Appointment</div>
                <div className="text-xs text-orange-600 mt-0.5">Select a date for the site visit or meeting.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="Appointment Date"
                type="date"
                value={formData.appointmentDate || ""}
                onChange={e => setFormData({ ...formData, appointmentDate: e.target.value })}
                required
              />
            </div>
            {commonNotes}
          </div>
        );

      case "site_visit":
        return (
          <SiteVisitWizard
            initialData={{
                ...account,
                propertyAddress: account.propertyAddress || account.address || account.addressLine1 || '',
                propertySize: account.propertySize || account.propertyType || '',
                projectType: account.projectType || '',
                propertyType: account.propertyType || ''
            }}
            account={account}
            fullName={fullName}
            commonNotes={commonNotes}
            onSubmit={(data) => {
              setFormData(data);
              processSubmission(data);
            }}
            onCancel={onClose}
            loading={loading}
          />
        );

      case "inspection_done":
        return (
          <InspectionAddForm
            initialData={account}
            onSubmit={(data) => {
              setFormData(data);
              processSubmission(data);
            }}
            onCancel={onClose}
            loading={loading}
          />
        );

      case "quotation":
      case "quotation_sent":
        return (
          <div className="space-y-4">
            <div className="p-4 bg-indigo-50 rounded-xl border border-indigo-100 flex items-start gap-3">
              <div className="p-2 bg-indigo-100 text-indigo-600 rounded-lg">
                <DollarSign size={20} />
              </div>
              <div>
                <div className="text-sm font-bold text-indigo-800">Send Quotation</div>
                <div className="text-xs text-indigo-600 mt-0.5">Enter the final quotation amount.</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <FormInput
                label="Capacity (kW)"
                type="number"
                value={formData.SDCapacity || ""}
                onChange={e => setFormData({ ...formData, SDCapacity: e.target.value })}
              />
              <FormInput
                label="Amount (₹)"
                type="number"
                value={formData.quotationAmount || ""}
                onChange={e => setFormData({ ...formData, quotationAmount: e.target.value })}
                required
              />
            </div>

            {commonNotes}
          </div>
        );

      case "project_started":
        return (
          <div className="space-y-4">
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-100 flex items-start gap-4">
              <div className="p-2.5 bg-emerald-100 text-emerald-600 rounded-xl">
                <ShieldCheck size={22} />
              </div>
              <div>
                <div className="text-sm font-bold text-emerald-900">Start Project Execution</div>
                <div className="text-xs text-emerald-700 mt-0.5 accounting-relaxed">
                  Before starting, please confirm if this project is eligible for a government subsidy.
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider pl-1">Subsidy Configuration</label>
              <div className="grid grid-cols-2 gap-2">
                {['yes', 'no'].map(val => (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setFormData({ ...formData, hasSubsidy: val })}
                    className={`p-3 rounded-xl border-2 font-bold text-xs transition-all ${formData.hasSubsidy === val
                        ? 'border-brand-blue bg-blue-50 text-brand-blue shadow-md'
                        : 'border-slate-100 bg-white text-slate-400 hover:border-slate-200'
                      }`}
                  >
                    {val === 'yes' ? 'With Subsidy' : 'No Subsidy'}
                  </button>
                ))}
              </div>
              {formData.hasSubsidy === 'yes' && (
                <FormInput
                  label="Subsidy ID / Reference *"
                  placeholder="Enter Government Subsidy ID..."
                  value={formData.subsidyId || ""}
                  onChange={e => setFormData({ ...formData, subsidyId: e.target.value })}
                  required
                />
              )}
            </div>
            {commonNotes}
          </div>
        );

      case "approved":
        return (
          <div className="space-y-4 text-center">
            <div className="mx-auto w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-2">
              <CheckCircle size={32} />
            </div>
            <div>
              <h4 className="font-bold text-slate-800">Approve Account?</h4>
              <p className="text-xs text-slate-500 mt-1">This will mark the account as approved and move it to the next stage of the project.</p>
            </div>
            {commonNotes}
          </div>
        );

      case "assigned":
        return (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 flex items-center gap-3">
              <User size={50} className="text-brand-blue" />
              <div className="flex flex-col gap-1">
                <div className="text-sm font-bold text-brand-blue">Assign Sales Executive</div>
                <p className="text-xs text-blue">You are moving this account to the Assigned stage. Please select an executive to take ownership.</p>
              </div>
            </div>
            <FormSelect
              label={isSalesExec ? "Assigned To (You)" : "Select Executive"}
              placeholder="Select Sales Executive"
              value={formData.assignedTo || ""}
              onChange={e => !isSalesExec && setFormData({ ...formData, assignedTo: e.target.value })}
              disabled={isSalesExec}
              options={salesExecs.map(u => ({ value: u._id, label: u.name }))}
              required
            />
            {commonNotes}
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 flex items-center gap-3">
              <Info size={18} className="text-slate-400" />
              <div className="text-sm font-bold text-slate-700">Transition to {stage.label}</div>
            </div>
            {commonNotes}
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={!!pendingStatus}
      onClose={onClose}
      title={`Transition: ${fullName}`}
      size={targetStatus === 'inspection_done' ? "lg" : "sm"}
    >
      <div className="space-y-5">
        <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
          <div className="w-10 h-10 rounded-full bg-brand-blue/10 flex items-center justify-center font-bold text-brand-blue uppercase">
            {fullName.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-brand-dark truncate">{fullName}</div>
            <div className="text-[10px] text-slate-400 font-medium">
              Currently: <span className="font-bold">{currentStatus.replace('_', ' ')}</span> → Target: <span className="text-brand-blue font-bold">{stage.label}</span>
            </div>
          </div>
        </div>
        {((targetStatus === 'quotation' || targetStatus === 'quotation_sent')) && (() => {
          if (isMissingEmail || isInvalidFormat || isSuspicious) {
            return (
              <div className={`p-3 rounded-xl border flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300 ${isSuspicious ? 'bg-amber-50 border-amber-100 text-amber-700' : 'bg-rose-50 border-rose-100 text-rose-600'}`}>
                <div className={`p-1.5 rounded-lg ${isSuspicious ? 'bg-amber-100' : 'bg-rose-100'}`}>
                  <AlertTriangle size={16} />
                </div>
                <div className="flex flex-col">
                  <span className="text-[11px] font-bold uppercase tracking-wider">
                    {isMissingEmail ? "Missing Email Address" : isInvalidFormat ? "Invalid Email Format" : "Suspicious Email Address"}
                  </span>
                  <span className="text-[10px] opacity-80 accounting-tight">
                    {isMissingEmail
                      ? "This customer has no email. No automated mail will be sent. You can download the file manually after confirming."
                      : isInvalidFormat
                        ? `The email "${account.email}" is invalid. Automated delivery will fail. Please download the file manually after confirming.`
                        : `The email "${account.email}" looks too short and might bounce. Please double-check it before sending.`
                    }
                  </span>
                </div>
              </div>
            );
          }
          return null;
        })()}

        <ConfirmationModal
          isOpen={showEmailConfirm}
          onClose={() => setShowEmailConfirm(false)}
          onConfirm={() => processSubmission(confirmData)}
          title="Proceed without automated email?"
          message={`We couldn't find a valid email for this customer. You can still generate the quotation and download it manually from the View Account modal after confirming.`}
          confirmText="Confirm & Download Manually"
          cancelText="Back"
          variant="warning"
        />

        {targetStatus === 'inspection_done' || targetStatus === 'site_visit' ? (
          renderFields()
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {renderFields()}

            <div className="flex gap-3 pt-2">
              <Button
                type="submit"
                className="flex-1"
                loading={loading}
                variant="orange"
              >
                Confirm {stage.label}
              </Button>
              <Button variant="secondary" onClick={onClose} className="px-6">Cancel</Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};




